import { create } from 'zustand';
import { CHAT_TYPING_TTL_MS } from '@nexops/shared';
import type { PendingMessage } from '../types';

interface ChatStoreState {
  /** Czy strumień SSE jest aktualnie połączony. */
  connected: boolean;
  /** conversationId → userId → moment wygaśnięcia wskaźnika „pisze…”. */
  typing: Record<string, Record<string, number>>;
  /** conversationId → wiadomości oczekujące na potwierdzenie / nieudane. */
  pending: Record<string, PendingMessage[]>;
  setConnected: (connected: boolean) => void;
  markTyping: (conversationId: string, userId: string) => void;
  clearTyping: (conversationId: string, userId: string) => void;
  addPending: (message: PendingMessage) => void;
  setPendingStatus: (conversationId: string, tempId: string, status: PendingMessage['status']) => void;
  removePending: (conversationId: string, tempId: string) => void;
}

const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Stan czatu spoza cache zapytań: połączenie, „pisze…”, wiadomości w drodze. */
export const useChatStore = create<ChatStoreState>()((set, get) => ({
  connected: false,
  typing: {},
  pending: {},
  setConnected: (connected) => set({ connected }),
  markTyping: (conversationId, userId) => {
    const key = `${conversationId}:${userId}`;
    clearTimeout(typingTimers.get(key));
    const expiresAt = Date.now() + CHAT_TYPING_TTL_MS;
    set((s) => ({ typing: { ...s.typing, [conversationId]: { ...s.typing[conversationId], [userId]: expiresAt } } }));
    typingTimers.set(
      key,
      setTimeout(() => {
        typingTimers.delete(key);
        if (get().typing[conversationId]?.[userId] === expiresAt) get().clearTyping(conversationId, userId);
      }, CHAT_TYPING_TTL_MS + 50),
    );
  },
  clearTyping: (conversationId, userId) =>
    set((s) => {
      const current = s.typing[conversationId];
      if (!current || !(userId in current)) return s;
      const rest = Object.fromEntries(Object.entries(current).filter(([id]) => id !== userId));
      const next = { ...s.typing };
      if (Object.keys(rest).length) next[conversationId] = rest;
      else delete next[conversationId];
      return { typing: next };
    }),
  addPending: (message) =>
    set((s) => ({ pending: { ...s.pending, [message.conversationId]: [...(s.pending[message.conversationId] ?? []), message] } })),
  setPendingStatus: (conversationId, tempId, status) =>
    set((s) => ({
      pending: {
        ...s.pending,
        [conversationId]: (s.pending[conversationId] ?? []).map((p) => (p.tempId === tempId ? { ...p, status } : p)),
      },
    })),
  removePending: (conversationId, tempId) =>
    set((s) => ({ pending: { ...s.pending, [conversationId]: (s.pending[conversationId] ?? []).filter((p) => p.tempId !== tempId) } })),
}));
